<template>
    <div v-if="consultation">
        <div class="card mb-3">
            <div class="card-header">利用制限の理由</div>
            <div class="card-body">
                <div class="font-weight-bold reason-text">{{ restriction_reason }}</div>
            </div>
        </div>

        <div class="card mb-3">
            <div class="card-header font-weight-bold">予約内容</div>
            <div class="card-body">
                <dl>
                    <dt>予約番号</dt>
                    <dd>
                        <span>#{{ consultation.id }}</span>
                        <span
                            v-if="consultation.flg_reserved_by_doctor === '1'"
                            class="small">
                            （医師による予約）
                        </span>
                    </dd>
                </dl>
                <dl>
                    <dt>{{ $locale.patient }}</dt>
                    <dd>
                        <template v-if="consultation.user">
                            <span>{{ consultation.user.full_name }}</span>
                            <span class="small">（{{ consultation.user.full_name_kana }}）</span>
                        </template>
                        <template v-else>
                            <span class="text-muted">退会済の{{ $locale.patient }}</span>
                        </template>
                    </dd>
                </dl>

                <dl>
                    <dt>{{ $locale.consultation_host }}科</dt>
                    <dd>{{ consultation.medical_course.name }}</dd>
                </dl>

                <dl>
                    <dt>予約日時</dt>
                    <dd>
                        <span>{{ helper.dateForHuman(consultation.start_time, doctor.timezone) }}</span>
                        <span
                            class="small font-weight-bold"
                            :class="helper.dayForHumanCSS(consultation.start_time, doctor.timezone)">
                            {{ helper.dayForHuman(consultation.start_time, doctor.timezone, { bracket: true }) }}
                        </span>
                        <span>{{ helper.timeForHuman(consultation, doctor.timezone) }}</span>
                        <timezone-box-jst
                            v-if="doctor.timezone !== 'Asia/Tokyo'"
                            :consultation="consultation"
                            :timezone="doctor.timezone">
                        </timezone-box-jst>
                    </dd>
                </dl>
            </div>
        </div>

        <div class="card">
            <div class="card-header">キャンセル事由</div>
            <div class="card-body">
                <div class="mb-4">
                    <label for="cancel_category">キャンセルの原因を選択してください。<span class="is-invalid">*</span></label>
                    <select
                        id="cancel_category"
                        style="min-width: 100px;"
                        class="form-control"
                        disabled>
                        <option value="">医師都合によるキャンセル</option>
                    </select>
                </div>
                <div class="mb-4">
                    <label for="cancel_reason">キャンセル事由を選択してください。<span class="is-invalid">*</span></label>
                    <select
                        v-model="cancel_reason"
                        id="cancel_reason"
                        style="min-width: 100px;"
                        class="form-control"
                        :class="{ 'is-invalid' : errors.cancel_reason }">
                        <option value="">選択してください</option>
                        <option
                            v-for="cancelReasonValue in cancel_reason_values">
                            {{ cancelReasonValue }}
                        </option>
                    </select>
                    <div v-if="errors.cancel_reason"
                        class="invalid-feedback d-block">
                        {{ errors.cancel_reason[0] }}
                    </div>
                </div>
                <div class="mb-4">
                    <label for="cancel_reason_detail">キャンセル事由の詳細を記入してください。<span class="is-invalid">*</span></label>
                    <textarea
                        v-model="cancel_reason_detail"
                        type="text"
                        id="cancel_reason_detail"
                        class="form-control"
                        :class="{ 'is-invalid' : errors.cancel_reason_detail }">
                    </textarea>
                    <div v-if="errors.cancel_reason_detail"
                        class="invalid-feedback d-block">
                        {{ errors.cancel_reason_detail[0] }}
                    </div>
                </div>
                <div class="mb-4">
                    <label for="message_to_user">今回の{{ $locale.consultation_host }}を予約された会員様へお伝えすることがあればご記入ください。</label>
                    <textarea
                        v-model="message_to_user"
                        type="text"
                        id="message_to_user"
                        class="form-control"
                        placeholder="自由にご記入ください。"
                        :class="{ 'is-invalid' : errors.message_to_user }">
                    </textarea>
                    <div v-if="errors.message_to_user"
                        class="invalid-feedback d-block">
                        {{ errors.message_to_user[0] }}
                    </div>
                </div>
                <div class="mb-4">
                    <label for="prevention_measures">今後同様の理由でのキャンセルを避けるための対策はありますか？</label>
                    <textarea
                        v-model="prevention_measures"
                        type="text"
                        id="prevention_measures"
                        class="form-control"
                        placeholder="ある方は対策をご記入ください。"
                        :class="{ 'is-invalid' : errors.prevention_measures }">
                    </textarea>
                    <div v-if="errors.prevention_measures"
                        class="invalid-feedback d-block">
                        {{ errors.prevention_measures[0] }}
                    </div>
                </div>
            </div>
        </div>

        <hr>

        <div class="row">
            <div class="col">
                <div class="text-center">
                    <p>キャンセル事由を送信します。<br>よろしいですか？</p>
                    <div :class="{ 'u-loader u-loader--sm' : inProgress }">
                        <div class="d-flex justify-content-center">
                            <button
                                class="btn btn-yokumiru-doctor"
                                @click="handleOk()">
                                キャンセル事由を送信する
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment-timezone'
    import constants from '../../constants'
    import YokumiruHelper from '../../YokumiruHelper'

    export default {
        props: {
            doctor: {
                type: Object,
                default: {},
            },
        },

        data: function () {
            return {
                helper: YokumiruHelper,
                modalShow: false,
                inProgress: false,
                statusConfirmed: constants.consultations.confirmed,
                cancel_reason: '',
                cancel_reason_detail: '',
                message_to_user: '',
                prevention_measures: '',
                errors: {},
                cancel_reason_values: [
                    '勤務先病院の都合のため',
                    'プライベートの事情や体調不良',
                    '予約を忘れていた',
                    '会員にキャンセルを頼まれた',
                    '日程を変更したいため',
                    'ご自身に関する特別な事情（重篤な病気、事故、自然災害）',
                    'その他',
                ],
                consultation: null,
                restriction_reason: '',
            }
        },

        created: async function () {
            await this.fetchDoctorNoReasonConsultation()

            if (this.consultation.status === constants.consultations.absenceDoctor) {
                this.restriction_reason = '医師による未入室終了'
            } else if (this.consultation.cancel_category === constants.cancelCategory.doctorLogin) {
                this.restriction_reason = '予約未確認による自動キャンセル'
            }
        },

        methods: {
            /**
             * 診療予約の取得
             */
            fetchDoctorNoReasonConsultation: async function () {
                await axios.get(`${constants.doctor.consultations.endpoint.doctorUsageRestriction}`)
                    .then(({data}) => {
                        this.consultation = data.data
                    })
            },

            /**
             * 予約ボタン押下時
             * 保存処理
             */
            handleOk: async function () {
                if (this.inProgress) return
                this.inProgress = true

                const endpoint = `${constants.doctor.consultations.endpoint.base}/${this.consultation.id}/cancel_reason_detail`
                const data = { 
                    cancel_reason: this.cancel_reason,
                    cancel_reason_detail: this.cancel_reason_detail,
                    message_to_user: this.message_to_user,
                    prevention_measures: this.prevention_measures,
                }

                await axios.post(endpoint, data)
                    .then(({data}) => {
                        window.location.href = '/doctor/home'
                    })
                    .catch(({response}) => {
                        if (response.data && response.data.hasOwnProperty('errors')) {
                            if (response.data.errors.hasOwnProperty('message')) {
                                bus.$emit('alert-danger', response.data.errors.message[0])
                            }
                            else {
                                this.errors = response.data.errors
                                bus.$emit('alert-danger')
                            }
                        }
                        else {
                            bus.$emit('alert-danger', 'システムエラーが発生しました。')
                        }
                    })

                this.inProgress = false
            },
        },
    }
</script>

<style scoped lang="scss">
    .reason-text {
        color: red;
    }

    span.is-invalid {
        position: relative;
        left: 2px;
        color: #dc3545;
    }
</style>

<style lang="scss">
    .fc-toolbar {
        .fc-center {
            h2 {
                font-size: 1.4rem;
                margin-bottom: 0;
            }
        }
    }
</style>
